import { AxiosResponse } from 'axios';

export interface idVerificationServiceInterface {
  getOrCreateSession(
    params: CreateSessionParams
  ): Promise<AxiosResponse<VerificationSessionResponse>>;
  getSessionStatus(
    params: GetSessionParams
  ): Promise<AxiosResponse<VerificationSessionResponse>>;
}

export interface AuthenticatedIdVerificationParams {
  accessToken: string;
}

export interface CreateSessionParams
  extends AuthenticatedIdVerificationParams {}

export interface GetSessionParams extends AuthenticatedIdVerificationParams {}

export interface VerificationSession {
  id: string;
  status: IdVerificationStatus;
  user_id: string;
  created_at: Date;
  updated_at: Date | null;
  url: string;
  vendor: VerificationVendor.DIDIT;
  data: VerificationData;
}

export interface VerificationSessionResponse {
  status_code: number;
  response_body: VerificationSession;
}

enum VerificationVendor {
  DIDIT = 'didit',
}

export enum IdVerificationStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  APPROVED = 'approved',
  DECLINED = 'declined',
  IN_REVIEW = 'in_review',
  EXPIRED = 'expired',
  ABANDONED = 'abandoned',
  KYC_EXPIRED = 'kyc_expired',
}

enum VerificationFeatures {
  OCR = 'OCR',
  OCR_NFC = 'OCR + NFC',
  OCR_AML = 'OCR + AML',
  OCR_NFC_AML = 'OCR + NFC + AML',
  OCR_FACE = 'OCR + FACE',
  OCR_NFC_FACE = 'OCR + NFC + FACE',
  OCR_FACE_AML = 'OCR + FACE + AML',
  OCR_NFC_FACE_AML = 'OCR + NFC + FACE + AML',
}

interface VerificationData {
  session_id: string;
  session_number: number;
  session_token: string;
  url: string;
  vendor_data: string;
  status: IdVerificationStatus;
  callback: string;
  features: VerificationFeatures;
  data: DiditVerificationData;
}

interface DiditVerificationData {
  kyc: {
    gender: string | null;
    status: string;
    address: string | null;
    full_name: string;
    last_name: string;
    back_image: string | null;
    back_video: string | null;
    created_at: string;
    first_name: string;
    ocr_status: string;
    front_image: string | null;
    front_video: string | null;
    nationality: string | null;
    date_of_birth: string;
    document_type: string;
    issuing_state: string;
    marital_status: string;
    parsed_address: string | null;
    place_of_birth: string;
    portrait_image: string | null;
    document_number: string;
    expiration_date: string | null;
    full_back_image: string | null;
    is_nfc_verified: boolean;
    personal_number: string | null;
    epassport_status: string | null;
    full_front_image: string | null;
    formatted_address: string | null;
    issuing_state_name: string;
  };
  aml: null;
  face: {
    status: string;
    video_url: string | null;
    source_image: string | null;
    target_image: string | null;
    liveness_status: string;
    face_match_status: string;
    liveness_confidence: number | null;
    face_match_similarity: number | null;
  };
  location: {
    isp: string | null;
    status: string;
    ip_city: string;
    ip_state: string;
    latitude: number;
    platform: string;
    longitude: number;
    os_family: string;
    time_zone: string;
    ip_address: string;
    ip_country: string;
    ip_location: {
      latitude: number;
      longitude: number;
    };
    device_brand: string;
    device_model: string;
    organization: string | null;
    is_vpn_or_tor: boolean;
    browser_family: string;
    is_data_center: boolean;
    ip_country_code: string;
    time_zone_offset: string;
    document_location: {
      latitude: number;
      longitude: number;
    };
    distance_from_document_to_ip_km: {
      distance: number;
      direction: string;
    };
  };
  warnings: {
    risk: string;
    feature: string;
    log_type: string;
    additional_data: {
      session_id?: string;
    } | null;
    long_description: string;
    short_description: string;
  }[];
}
